import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import FarmersNav from "@/components/FarmersNav";
import { http } from "@/lib/api";
import { fpPath } from "@/lib/site";
import {
  Newspaper, MagnifyingGlass, ArrowSquareOut, Archive, Sparkle,
  Clock, ArrowLeft, CheckCircle, BookmarkSimple, GlobeHemisphereWest,
  EnvelopeSimple, PaperPlaneTilt,
} from "@phosphor-icons/react";

/**
 * FarmersNewsPage — /farmers/news · agriculture news continuously
 * ingested from multiple sources (per-region), filter chips by topic
 * and region, and a "saved" mode that stashes cards for later reading.
 * Broken links land in the archive.
 */
const TOPIC_LABELS = {
  agriculture:    "All agriculture",
  crops:          "Crops",
  farmers:        "Farmers",
  weather:        "Weather",
  "food-security":"Food security",
  livestock:      "Livestock",
  policy:         "Policy",
  sustainability: "Sustainability",
};

const SAVED_KEY = "fp:news-saved";

function loadSaved() {
  try { return JSON.parse(localStorage.getItem(SAVED_KEY) || "[]"); }
  catch { return []; }
}
function persistSaved(items) {
  try { localStorage.setItem(SAVED_KEY, JSON.stringify(items)); } catch { /* ignore */ }
}

function DigestSubscribeCard() {
  const [email, setEmail] = useState("");
  const [region, setRegion] = useState("global");
  const [crops, setCrops] = useState([]);          // selected crop slugs
  const [cropOptions, setCropOptions] = useState([]);
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    http.get("/farmers/news/digest/crops")
      .then((r) => { if (!cancelled) setCropOptions(r?.data?.crops || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const toggleCrop = (slug) => {
    setCrops((prev) => prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setState("error");
      setMessage("Enter a valid email.");
      return;
    }
    setState("sending");
    setMessage("");
    try {
      await http.post("/farmers/news/digest/subscribe", {
        email: email.trim().toLowerCase(),
        region,
        crops,
      });
      setState("done");
      const cropTxt = crops.length ? ` · ${crops.length} crop${crops.length === 1 ? "" : "s"}` : "";
      setMessage(`You're in — Monday morning digest for ${region === "global" ? "Global" : region.toUpperCase()}${cropTxt} on its way.`);
    } catch (err) {
      setState("error");
      const detail = err?.response?.data?.detail;
      setMessage(typeof detail === "string" ? detail : "Something broke on our side — try again in a minute.");
    }
  };

  return (
    <section
      data-testid="news-digest-subscribe"
      className="mt-6 rounded-3xl border border-[color:var(--np-brand,#B85F2A)]/25 bg-white p-5 md:p-7 flex flex-col md:flex-row gap-5 items-start md:items-center"
    >
      <div className="flex-1 min-w-0">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] inline-flex items-center gap-1">
          <EnvelopeSimple size={11} weight="fill" /> Weekly Digest
        </div>
        <div className="mt-1 font-serif-display text-2xl md:text-3xl leading-tight text-[#151312]">
          Top 5 stories every Monday morning.
        </div>
        <p className="mt-1.5 text-sm text-[#3A3830]">
          Get the biggest agriculture stories in your region — Global, India, Africa or EU — landing in your inbox before you head to the field.
        </p>
      </div>
      <form
        onSubmit={submit}
        className="w-full md:w-auto md:min-w-[420px] flex flex-col gap-2"
        data-testid="news-digest-form"
      >
        <div className="flex flex-wrap gap-1 mb-1" data-testid="news-digest-region-picker">
          {[
            { key: "global", label: "Global" },
            { key: "india",  label: "India"  },
            { key: "africa", label: "Africa" },
            { key: "eu",     label: "EU"     },
          ].map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRegion(r.key)}
              data-testid={`news-digest-region-${r.key}`}
              className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase transition ${region === r.key ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent" : "border-black/15 bg-white text-[#241209] hover:bg-black/5"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        {cropOptions.length > 0 && (
          <div className="mt-1" data-testid="news-digest-crop-picker">
            <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#8A857A] mb-1">
              Filter by crops (optional)
            </div>
            <div className="flex flex-wrap gap-1">
              {cropOptions.map((c) => (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => toggleCrop(c.slug)}
                  data-testid={`news-digest-crop-${c.slug}`}
                  className={`rounded-full border px-2.5 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase transition ${crops.includes(c.slug) ? "bg-[#2D8659] text-white border-transparent" : "border-black/12 bg-white text-[#3A3830] hover:bg-black/5"}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-1">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@farm.com"
            required
            data-testid="news-digest-email"
            className="flex-1 min-w-[220px] rounded-full border border-black/15 bg-white px-4 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
          />
          <button
            type="submit"
            disabled={state === "sending"}
            data-testid="news-digest-submit"
            className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-60 hover:opacity-95"
          >
            <PaperPlaneTilt size={11} weight="fill" />
            {state === "sending" ? "Sending…" : state === "done" ? "Subscribed" : "Send me Mondays"}
          </button>
        </div>
        {message && (
          <div
            data-testid="news-digest-msg"
            className={`text-[11px] font-mono-tech tracking-[0.24em] uppercase ${state === "done" ? "text-[color:var(--np-brand,#2D8659)]" : "text-[color:var(--np-brand,#B85F2A)]"}`}
          >
            {message}
          </div>
        )}
      </form>
    </section>
  );
}


export default function FarmersNewsPage() {
  const [mode, setMode] = useState("live");     // live | saved | archive
  const [topic, setTopic] = useState("all");
  const [region, setRegion] = useState("all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [topics, setTopics] = useState([]);
  const [regions, setRegions] = useState([]);
  const [saved, setSaved] = useState(loadSaved);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const savedIds = new Set(saved.map((s) => s.id));

  const toggleSave = (article) => {
    const exists = savedIds.has(article.id);
    const next = exists
      ? saved.filter((s) => s.id !== article.id)
      : [{ ...article, saved_at: new Date().toISOString() }, ...saved].slice(0, 200);
    setSaved(next);
    persistSaved(next);
  };

  // debounce q
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (mode === "saved") {
      setRows(saved); setTotal(saved.length); setLoading(false); setError(null);
      return;
    }
    let cancel = false;
    setLoading(true); setError(null);
    const path = mode === "archive" ? "/farmers/news/archive" : "/farmers/news";
    const params = new URLSearchParams({ limit: "36" });
    if (mode === "live" && topic !== "all")  params.set("topic", topic);
    if (mode === "live" && region !== "all") params.set("region", region);
    if (mode === "live" && debouncedQ)       params.set("q", debouncedQ);
    http.get(`${path}?${params.toString()}`)
      .then((r) => { if (cancel) return;
        setRows(r.data.articles || []);
        setTotal(r.data.total || 0);
      })
      .catch((e) => !cancel && setError(e?.message || "Could not load news."))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [mode, topic, region, debouncedQ, saved]);

  // Topic + region chips (live mode only)
  useEffect(() => {
    if (mode !== "live") return;
    http.get("/farmers/news/topics").then((r) => setTopics(r.data.topics || [])).catch(() => {});
    http.get("/farmers/news/regions").then((r) => setRegions(r.data.regions || [])).catch(() => {});
  }, [mode]);

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]" data-testid="farmers-news-page">
      <div className="max-w-6xl mx-auto"><FarmersNav /></div>

      <div className="max-w-6xl mx-auto mt-6">
        <Link
          to={fpPath("/")}
          className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]"
          data-testid="farmers-news-back"
        >
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>

        <header className="mt-3 flex items-baseline gap-3 flex-wrap">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              <Newspaper size={12} weight="fill" className="inline mr-1" /> Farmers Pulse News
            </div>
            <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight text-[#151312]">
              Farming news, all in one feed.
            </h1>
            <p className="mt-2 max-w-2xl text-[#3A3830]">
              Continuously gathered from farms, crops, food-security, weather, livestock,
              sustainability and policy sources across four regions. Bookmark any story
              for later. Broken links move to the archive so nothing gets lost.
            </p>
          </div>
        </header>

        <DigestSubscribeCard />

        {/* Mode tabs */}
        <div className="mt-6 inline-flex items-center gap-1 rounded-full bg-[#151312] p-1" data-testid="news-mode-tabs">
          <ModeTab active={mode === "live"}    onClick={() => setMode("live")}
                   testid="news-mode-live"    label="Live" Icon={Sparkle} />
          <ModeTab active={mode === "saved"}   onClick={() => setMode("saved")}
                   testid="news-mode-saved"   label={`Saved · ${saved.length}`} Icon={BookmarkSimple} />
          <ModeTab active={mode === "archive"} onClick={() => setMode("archive")}
                   testid="news-mode-archive" label="Archive" Icon={Archive} />
        </div>

        {mode === "live" && (
          <>
            {/* Region chips */}
            <div className="mt-5 flex flex-wrap gap-2" data-testid="news-region-chips">
              <span className="inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#6C685E] mr-1">
                <GlobeHemisphereWest size={10} weight="fill" /> Region
              </span>
              <TopicChip active={region === "all"} onClick={() => setRegion("all")}
                         label="All" testid="news-region-all" />
              {regions.map((r) => (
                <TopicChip key={r.key}
                  active={region === r.key}
                  onClick={() => setRegion(r.key)}
                  label={`${r.label} · ${r.count}`}
                  testid={`news-region-${r.key}`} />
              ))}
            </div>

            {/* Search + topic chips */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 rounded-full bg-white border border-black/10 px-4 py-2 shadow-sm">
                <MagnifyingGlass size={14} weight="duotone" className="text-[color:var(--np-brand,#B85F2A)]" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search farm news…"
                  data-testid="news-search"
                  className="bg-transparent outline-none text-sm w-64 max-w-full text-[#151312] placeholder:text-[#8A857A]"
                />
              </label>
              <span className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#6C685E]">
                {loading ? "Loading…" : `${total} live · ${rows.length} showing`}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap gap-2" data-testid="news-topic-chips">
              <TopicChip active={topic === "all"} onClick={() => setTopic("all")} label="All topics" />
              {topics.map((t) => (
                <TopicChip
                  key={t.key}
                  active={topic === t.key}
                  onClick={() => setTopic(t.key)}
                  label={`${TOPIC_LABELS[t.key] || t.key} · ${t.count}`}
                  testid={`news-topic-${t.key}`}
                />
              ))}
            </div>
          </>
        )}

        {mode === "saved" && saved.length > 0 && (
          <p className="mt-4 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#6C685E]">
            {saved.length} saved · stored on this device
          </p>
        )}

        {/* Error / empty / loading */}
        {error && (
          <div className="mt-6 rounded-2xl border border-[color:var(--np-brand,#B85F2A)]/25 bg-[color:var(--np-brand,#B85F2A)]/8 text-[color:var(--np-brand,#B85F2A)] p-4 text-sm">
            {error}
          </div>
        )}
        {loading && mode !== "saved" && (
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="news-loading">
            {[0,1,2,3,4,5].map((i) => <div key={i} className="h-40 rounded-2xl bg-black/5 animate-pulse" />)}
          </div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div className="mt-6 rounded-2xl border border-dashed border-black/20 bg-white/60 p-8 text-center" data-testid="news-empty">
            <Newspaper size={22} weight="duotone" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
            <div className="font-serif-display text-2xl text-[#151312]">
              {mode === "saved"   && "Nothing saved yet — tap the bookmark on any story."}
              {mode === "archive" && "Nothing archived yet."}
              {mode === "live"    && "News is being ingested — check back in a few minutes."}
            </div>
          </div>
        )}

        {/* Article grid */}
        {!loading && !error && rows.length > 0 && (
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="news-list">
            {rows.map((a) => (
              <ArticleCard
                key={a.id} a={a}
                archived={mode === "archive"}
                savedMode={mode === "saved"}
                isSaved={savedIds.has(a.id)}
                onToggleSave={() => toggleSave(a)}
              />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

function ModeTab({ active, onClick, label, Icon, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase transition ${
        active ? "bg-[color:var(--np-brand,#B85F2A)] text-white" : "text-[#E7DDCA] hover:text-white"
      }`}
    >
      <Icon size={11} weight="fill" /> {label}
    </button>
  );
}

function TopicChip({ active, onClick, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase transition ${
        active
          ? "bg-[#151312] text-[#F5EBDC] border-transparent"
          : "bg-white text-[#3A3830] border-black/10 hover:border-[color:var(--np-brand,#B85F2A)]"
      }`}
    >
      {label}
    </button>
  );
}

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 3600)    return `${Math.max(1, Math.round(diff / 60))} min ago`;
    if (diff < 86400)   return `${Math.round(diff / 3600)} h ago`;
    if (diff < 604800)  return `${Math.round(diff / 86400)} d ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

function ArticleCard({ a, archived, savedMode, isSaved, onToggleSave }) {
  const timestamp = archived ? a.archived_at : (savedMode ? a.saved_at : a.discovered_at);
  return (
    <article
      data-testid={`news-card-${a.id}`}
      className="rounded-2xl border border-black/10 bg-white p-4 flex flex-col gap-2 hover:border-[color:var(--np-brand,#B85F2A)] transition"
    >
      <div className="flex items-center gap-2">
        <span className="grid place-items-center h-8 w-8 rounded-full bg-[#F5EBDC] border border-black/10">
          <Newspaper size={14} weight="duotone" className="text-[color:var(--np-brand,#B85F2A)]" />
        </span>
        <span className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#6C685E] truncate">
          {a.source_name || "Source"}
        </span>
        <span className="ml-auto inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#6C685E]">
          <Clock size={10} weight="fill" /> {formatRelative(timestamp)}
        </span>
      </div>
      <h3 className="font-serif-display text-lg text-[#151312] leading-tight">{a.title}</h3>
      {a.summary && <p className="text-sm text-[#3A3830] leading-snug line-clamp-3">{a.summary}</p>}
      <div className="mt-1 flex items-center gap-2 flex-wrap">
        {(a.regions || []).slice(0, 2).map((r) => (
          <span key={r} className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#6C685E]">
            @{r}
          </span>
        ))}
        {(a.topics || []).slice(0, 2).map((t) => (
          <span key={t} className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            #{t}
          </span>
        ))}
      </div>
      <div className="pt-2 border-t border-black/10 flex items-center gap-2">
        {archived ? (
          <span className="inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#6C685E]">
            <Archive size={10} weight="fill" /> Link broken · headline preserved
          </span>
        ) : (
          <a
            href={a.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`news-open-${a.id}`}
            className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:opacity-80"
          >
            <CheckCircle size={11} weight="fill" /> Read on {a.source_name?.split(" ")[0] || "source"}
            <ArrowSquareOut size={11} weight="bold" />
          </a>
        )}
        <button
          onClick={onToggleSave}
          data-testid={`news-save-${a.id}`}
          className={`ml-auto inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase transition ${
            isSaved
              ? "bg-[color:var(--np-brand,#B85F2A)] text-white"
              : "border border-black/15 text-[#3A3830] hover:border-[color:var(--np-brand,#B85F2A)]"
          }`}
          title={isSaved ? "Remove from saved" : "Save for later"}
        >
          <BookmarkSimple size={10} weight={isSaved ? "fill" : "regular"} />
          {isSaved ? "Saved" : "Save"}
        </button>
      </div>
    </article>
  );
}
